import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HelpCircle, Instagram, MessageSquare, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { RevealLine, ScrollReveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Qa() {
  const { user, status } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [askText, setAskText] = useState("");
  const [asking, setAsking] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [answeringId, setAnsweringId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/qa");
      setQuestions(data);
    } catch {
      /* list is public; silent fail keeps page usable */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ask = async (e) => {
    e.preventDefault();
    if (!askText.trim()) return;
    setAsking(true);
    try {
      await api.post("/qa", { question: askText });
      setAskText("");
      await load();
      toast.success("Question posted — an admin will answer soon");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAsking(false);
    }
  };

  const answer = async (qaId) => {
    const text = (drafts[qaId] || "").trim();
    if (!text) return;
    try {
      await api.put(`/qa/${qaId}/answer`, { answer: text });
      setAnsweringId(null);
      setDrafts((prev) => ({ ...prev, [qaId]: "" }));
      await load();
      toast.success("Answer published");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (qaId) => {
    if (!window.confirm("Remove this question?")) return;
    try {
      await api.delete(`/qa/${qaId}`);
      setQuestions((prev) => prev.filter((q) => q.qa_id !== qaId));
      toast.success("Question removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openCount = questions.filter((q) => !q.answer).length;

  return (
    <div className="min-h-screen" data-testid="qa-page">
      <Navbar />
      <main className="mx-auto max-w-3xl px-6 pb-32 pt-40 md:pt-48">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-xs font-bold uppercase tracking-[0.35em] text-primary"
        >
          Ask the naturalists
        </motion.p>
        <h1 className="mt-6 font-serif text-6xl font-semibold leading-[0.95] tracking-tighter md:text-8xl" data-testid="qa-title">
          <RevealLine delay={0.15}>Q &amp; A<span className="text-primary">.</span></RevealLine>
        </h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground"
        >
          Curious about a campus creature, a photo, or a conservation project? Ask below — our admins answer every question publicly so everyone learns.
        </motion.p>

        {/* Ask box */}
        <div className="mt-14" data-testid="qa-ask-section">
          {status === "authed" ? (
            <form onSubmit={ask} className="border border-border bg-card p-6 md:p-8" data-testid="qa-ask-form">
              <label className="mb-3 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                Ask a question as {user?.name}
              </label>
              <textarea
                value={askText}
                onChange={(e) => setAskText(e.target.value)}
                data-testid="qa-ask-input"
                rows={3}
                maxLength={1000}
                placeholder="What did you spot? What are you wondering about?"
                className="w-full resize-none border border-border bg-background p-4 text-sm leading-relaxed outline-none transition-colors focus:border-primary"
              />
              <button
                type="submit"
                disabled={asking || !askText.trim()}
                data-testid="qa-ask-submit"
                className="mt-4 flex items-center gap-2 bg-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
                {asking ? "Posting…" : "Post question"}
              </button>
            </form>
          ) : (
            <div className="border border-dashed border-border p-8 text-center" data-testid="qa-signin-prompt">
              <HelpCircle className="mx-auto h-6 w-6 text-primary" />
              <p className="mt-3 text-sm text-muted-foreground">Sign in to ask a question — anyone can read the answers.</p>
              <Link
                to="/login"
                data-testid="qa-signin-link"
                className="mt-4 inline-block border border-foreground px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:bg-foreground hover:text-background"
              >
                Sign in
              </Link>
            </div>
          )}
        </div>

        {/* Question list */}
        <div className="mt-16 border-t border-border pt-10" data-testid="qa-list">
          <div className="mb-10 flex items-end justify-between">
            <h2 className="font-serif text-3xl font-semibold tracking-tight">All questions</h2>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground" data-testid="qa-counts">
              {questions.length} asked · {openCount} awaiting answers
            </p>
          </div>

          {questions.length === 0 ? (
            <p className="font-serif text-xl italic text-muted-foreground" data-testid="qa-empty">
              No questions yet — be the first to ask.
            </p>
          ) : (
            <div className="space-y-8">
              {questions.map((q, i) => {
                const canRemove = user && (user.role === "admin" || user.user_id === q.asked_by_id);
                return (
                  <ScrollReveal key={q.qa_id} delay={Math.min(i, 3) * 0.06}>
                    <article className="border border-border bg-card p-6 md:p-8" data-testid={`qa-item-${q.qa_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <MessageSquare className="mt-1 h-4 w-4 shrink-0 text-primary" />
                          <div>
                            <p className="text-base font-semibold leading-relaxed">{q.question}</p>
                            <p className="mt-2 text-xs text-muted-foreground">
                              {q.asked_by_name} · {fmtDate(q.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          {!q.answer && (
                            <span className="border border-primary/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-primary" data-testid={`qa-open-badge-${q.qa_id}`}>
                              Awaiting answer
                            </span>
                          )}
                          {canRemove && (
                            <button
                              onClick={() => remove(q.qa_id)}
                              data-testid={`qa-delete-${q.qa_id}`}
                              title="Remove question"
                              className="text-muted-foreground transition-colors hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {q.answer ? (
                        <div className="ml-7 mt-5 border-l-2 border-primary pl-5" data-testid={`qa-answer-${q.qa_id}`}>
                          <p className="text-sm leading-relaxed text-foreground/85">{q.answer}</p>
                          <p className="mt-2 text-xs font-bold uppercase tracking-[0.15em] text-primary">
                            Answered by {q.answered_by_name} · {fmtDate(q.answered_at)}
                          </p>
                        </div>
                      ) : user?.role === "admin" ? (
                        answeringId === q.qa_id ? (
                          <div className="ml-7 mt-5">
                            <textarea
                              value={drafts[q.qa_id] || ""}
                              onChange={(e) => setDrafts((prev) => ({ ...prev, [q.qa_id]: e.target.value }))}
                              data-testid={`qa-answer-input-${q.qa_id}`}
                              rows={3}
                              placeholder="Write the answer…"
                              className="w-full resize-none border border-border bg-background p-4 text-sm leading-relaxed outline-none transition-colors focus:border-primary"
                            />
                            <div className="mt-3 flex gap-3">
                              <button
                                onClick={() => answer(q.qa_id)}
                                data-testid={`qa-answer-submit-${q.qa_id}`}
                                className="bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5"
                              >
                                Publish answer
                              </button>
                              <button
                                onClick={() => setAnsweringId(null)}
                                data-testid={`qa-answer-cancel-${q.qa_id}`}
                                className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAnsweringId(q.qa_id)}
                            data-testid={`qa-answer-open-${q.qa_id}`}
                            className="ml-7 mt-5 border border-foreground px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:bg-foreground hover:text-background"
                          >
                            Answer this
                          </button>
                        )
                      ) : null}
                    </article>
                  </ScrollReveal>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Links strip */}
      <footer className="border-t border-border bg-foreground text-background" data-testid="qa-footer">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 py-10 md:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-background/50">Links</p>
          <a
            href="https://instagram.com/ck_findingfauna"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="qa-instagram-link"
            className="flex items-center gap-2 text-sm transition-colors duration-300 hover:text-primary"
          >
            <Instagram className="h-4 w-4" />
            @ck_findingfauna
          </a>
        </div>
      </footer>
    </div>
  );
}
